import { ArrowLeft } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { Page } from "@/components/layout/page";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader } from "@/components/ui/card";
import { EmptyState, ErrorState, Spinner } from "@/components/ui/feedback";
import { NewCaseButton } from "@/features/accident-person";
import { SamePhoneRecords } from "@/features/same-phone";
import { contactName } from "@/lib/contacts";
import { useContact, usePerson } from "@/lib/queries";
import type { Person } from "@/lib/types";
import { formatDateTime, formatPhoneNumber } from "@/lib/utils";

/**
 * A person's page (ACL-326 step 7, ADR-224 in the platform repo): their name and phone, their cases
 * newest first (each a record), the accidents those cases are from, and New case made from the newest
 * open case. Reached from any of their records; there is no list of people yet.
 */
export function PersonPage() {
  const { personId } = useParams<{ personId: string }>();
  const person = usePerson(personId);
  const source = useContact(newestOpenCase(person.data));

  if (person.isLoading) return <Page><Spinner /></Page>;
  if (person.error || !person.data) {
    return (
      <Page>
        <ErrorState error={person.error} title="Could not open this person" />
      </Page>
    );
  }
  const found = person.data;
  const name = contactName(found) ?? "No name";
  return (
    <Page className="space-y-5">
      <Link to="/contacts" className="inline-flex items-center gap-1 text-xs font-medium text-accent-11 hover:underline">
        <ArrowLeft className="size-3.5" aria-hidden />
        Contacts
      </Link>
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        <div className="flex min-w-0 flex-wrap items-center gap-x-4.5 gap-y-1.5">
          <h1 className="text-2xl font-semibold leading-8 tracking-tight text-text">{name}</h1>
          <span className="font-mono text-lg text-gray-11">{formatPhoneNumber(found.phone)}</span>
          <Badge tone="neutral">{caseCount(found)}</Badge>
        </div>
        {source.data && <NewCaseButton source={source.data} />}
      </div>

      <Card>
        <CardHeader title="Cases" description="Each case is a record of this person from one accident, newest first." />
        <div className="px-5 pb-4">
          {found.cases.length === 0 ? (
            <EmptyState title="No case you can open" description={notShownText(found)} />
          ) : (
            <>
              <SamePhoneRecords siblings={found.cases} phone={found.phone} caveat={false} label="Cases" />
              {found.cases_not_shown > 0 && <p className="mt-2 text-xs text-text-muted">{notShownText(found)}</p>}
            </>
          )}
        </div>
      </Card>

      <Card>
        <CardHeader title="Accidents" description="The accidents these cases are from." />
        <div className="px-5 pb-4">
          {found.accidents.length === 0 ? (
            <p className="text-sm text-text-muted">None yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {found.accidents.map((accident) => (
                <li key={accident.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <span className="font-medium text-text">{accident.number}</span>
                  <span className="text-xs text-text-muted">
                    {accident.cases} {accident.cases === 1 ? "case" : "cases"} · since {formatDateTime(accident.created_at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>
    </Page>
  );
}

/** The newest case still open and the reader's to open: what New case is made from. Pure. */
export function newestOpenCase(person: Pick<Person, "cases"> | undefined): string | undefined {
  return person?.cases.find((c) => c.open !== false && c.may_open && !c.deleted)?.id ?? person?.cases[0]?.id;
}

export function caseCount(person: Pick<Person, "cases" | "cases_not_shown">): string {
  const total = person.cases.length + person.cases_not_shown;
  return `${total} ${total === 1 ? "case" : "cases"}`;
}

export function notShownText(person: Pick<Person, "cases_not_shown">): string {
  const n = person.cases_not_shown;
  return n === 0 ? "" : `${n} ${n === 1 ? "case" : "cases"} not in your contacts, so not shown.`;
}
